import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ApiReward {
  _id: string;
  priceName: { en: string; ar: string };
  image: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RewardListFilters {
  search?: string;
  isActive?: boolean;
}

export interface RewardListResponse {
  success: boolean;
  code?: number;
  data: { list: ApiReward[]; total: number; page: number; limit: number; totalPages: number };
}

export interface RewardGetResponse {
  success: boolean;
  code?: number;
  data: ApiReward;
}

export interface RewardMutationResponse {
  success: boolean;
  code?: number;
  message?: string;
  data?: ApiReward;
}

export interface RewardToggleResponse {
  success: boolean;
  code?: number;
  message?: string;
  data?: { isActive: boolean };
}

export interface RewardPayload {
  priceNameEn?: string;
  priceNameAr?: string;
  isActive?: boolean;
}

const BASE = `${environment.apiUrl}/api/admin/reward`;

@Injectable({ providedIn: 'root' })
export class RewardsService {
  private http = inject(HttpClient);

  list(page = 1, limit = 20, filters: RewardListFilters = {}): Observable<RewardListResponse> {
    return this.http.post<RewardListResponse>(`${BASE}/list`, { page, limit, ...filters });
  }

  get(id: string): Observable<RewardGetResponse> {
    return this.http.post<RewardGetResponse>(`${BASE}/get`, { id });
  }

  create(payload: RewardPayload, image?: File | null): Observable<RewardMutationResponse> {
    return this.http.post<RewardMutationResponse>(`${BASE}/create`, this.toFormData(payload, image));
  }

  update(id: string, payload: RewardPayload, image?: File | null): Observable<RewardMutationResponse> {
    return this.http.post<RewardMutationResponse>(`${BASE}/update`, this.toFormData({ ...payload }, image, id));
  }

  toggle(id: string): Observable<RewardToggleResponse> {
    return this.http.post<RewardToggleResponse>(`${BASE}/toggle`, { id });
  }

  imageUrl(path: string | null | undefined): string | null {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${environment.apiUrl}/${path.replace(/^\//, '')}`;
  }

  private toFormData(payload: RewardPayload, image?: File | null, id?: string): FormData {
    const form = new FormData();
    if (id) form.append('id', id);
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) form.append(key, String(value));
    });
    if (image) form.append('image', image);
    return form;
  }
}
